/**
 * TRT (Testosterone Replacement Therapy) program domain logic.
 *
 * Pure, framework-agnostic helpers that derive a patient's pipeline column,
 * dose status, and the most recent qADAM / lab / vitals dates from the synced
 * trt_data payload. Date math reuses the shared dateUtils helpers so it stays
 * consistent with the rest of the app (local-timezone, YYYY-MM-DD strings).
 *
 * Synced trt_data shape (collected by the extension, see SyncTRTModal.vue):
 * {
 *   medications?: [{ drugName, sig, startDate, stopDate, isActive }],
 *   documents?:   [{ name, documentDate }],
 *   labs?: {
 *     orders?:  [{ orderedDate, tests: string[], ... }],
 *     results?: [{ receivedDate, summary, ... }]
 *   }
 * }
 */

import { getLocalToday } from './dateUtils';

// --- Constants --------------------------------------------------------------

export const COLUMN = {
  ONBOARDING: 'onboarding',
  ACTIVE: 'active',
  UPCOMING: 'upcoming',
  INACTIVE: 'inactive'
};

// Days-until-monitor-date window. A patient on a dose whose next monitor date
// falls within this window is "Upcoming"; it's also the freshness window for
// the vitals/qADAM/labs tags (an item dated more than this many days before the
// monitor date is considered stale).
export const MONITOR_WINDOW_DAYS = 21;

// Ordered list used to lay out the board left-to-right. Each entry carries the
// display label, the StatusBadge tone used for the column accent, and an
// accent color (drawn from the shared status palette) for the header bar.
export const COLUMNS = [
  { key: COLUMN.ONBOARDING, label: 'Onboarding', tone: 'neutral', accent: '#6b7280', description: 'No TRT dose yet' },
  { key: COLUMN.ACTIVE, label: 'Active', tone: 'good', accent: '#84ab51', description: 'Currently on a TRT dose' },
  { key: COLUMN.UPCOMING, label: 'Upcoming', tone: 'warning', accent: '#b8860b', description: `Monitor date within ${MONITOR_WINDOW_DAYS} days` },
  { key: COLUMN.INACTIVE, label: 'Inactive', tone: 'cancelled', accent: '#9ca3af', description: 'Only a past TRT dose' }
];

// --- Synced medication helpers ----------------------------------------------

/** Testosterone medications from a synced trt_data payload, newest first. */
function getTestosteroneMedications(trtData) {
  const meds = trtData && Array.isArray(trtData.medications) ? trtData.medications : [];
  return meds
    .filter((m) => (m.drugName || '').toLowerCase().includes('testosterone'))
    .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || ''));
}

/**
 * Pick the testosterone medication to display for a patient. Prefers an active
 * script and, among candidates, the most recently started one. Returns null
 * when the patient has no testosterone medication.
 * @param {{ medications?: Array<{ drugName?: string, isActive?: boolean, startDate?: string }> } | null | undefined} trtData
 */
export function getTestosteroneMedication(trtData) {
  const tMeds = getTestosteroneMedications(trtData);
  if (!tMeds.length) return null;
  const active = tMeds.filter((m) => m.isActive);
  return (active.length ? active : tMeds)[0];
}

/**
 * A patient's TRT dose status derived from their synced medications:
 *   - 'current' — actively on a testosterone dose (an active script)
 *   - 'past'    — only ever had a testosterone dose in the past (all stopped)
 *   - 'none'    — never had a testosterone dose
 * @param {{ medications?: unknown[] } | null | undefined} trtData
 * @returns {'current' | 'past' | 'none'}
 */
export function getTrtDoseStatus(trtData) {
  const tMeds = getTestosteroneMedications(trtData);
  if (!tMeds.length) return 'none';
  return tMeds.some((m) => m.isActive) ? 'current' : 'past';
}

/**
 * Parse the injected cc dose out of a medication sig, e.g.
 * "Inject 1.0cc IM once weekly" -> 1. Returns null when no cc value is present.
 */
export function parseTDoseCc(sig) {
  if (!sig) return null;
  const m = String(sig).match(/(\d+(?:\.\d+)?)\s*cc\b/i);
  return m ? parseFloat(m[1]) : null;
}

/**
 * Card dose label formatted as "1.0cc". Falls back to the raw sig when the cc
 * dose can't be parsed, or null when there's nothing to show.
 */
export function formatTrtDose(med) {
  if (!med) return null;
  const cc = parseTDoseCc(med.sig);
  if (cc == null) return med.sig || null;
  return `${cc.toFixed(1)}cc`;
}

// --- Synced document & lab helpers ------------------------------------------

/**
 * Pick the most recent qADAM questionnaire from a synced trt_data payload:
 * the document whose name mentions ADAM (matches "ADAM", "QADAM",
 * "Quantitative ADAM") with the latest documentDate. Returns null when none.
 * @param {{ documents?: Array<{ name?: string, documentDate?: string }> } | null | undefined} trtData
 */
export function getMostRecentAdamDocument(trtData) {
  const docs = trtData && Array.isArray(trtData.documents) ? trtData.documents : [];
  const adam = docs.filter((d) => /adam/i.test(d.name || ''));
  if (!adam.length) return null;
  return adam.reduce((latest, d) => ((d.documentDate || '') > (latest.documentDate || '') ? d : latest));
}

/**
 * Derive the testosterone lab status from a synced trt_data payload. Compares
 * the latest testosterone *result* against the latest testosterone *request*:
 *   - result exists and is newer-or-equal to the request -> 'received' (result date)
 *   - the request is newer (or there's no result yet)     -> 'requested' (request date)
 *   - neither a request nor a result exists               -> 'none'
 * Requests come from lab orders (structured `tests` names); results are matched
 * on the raw `summary` prose since results carry no structured test name. The
 * matched `request`/`result` records are returned too so the UI can show details.
 * @param {{ labs?: { orders?: Array<{ orderedDate?: string, tests?: string[] }>, results?: Array<{ receivedDate?: string, summary?: string }> } } | null | undefined} trtData
 * @returns {{ status: 'received' | 'requested' | 'none', date: string | null, request: object | null, result: object | null }}
 */
export function getTestosteroneLabStatus(trtData) {
  const labs = trtData && trtData.labs ? trtData.labs : {};
  const orders = Array.isArray(labs.orders) ? labs.orders : [];
  const results = Array.isArray(labs.results) ? labs.results : [];

  const latest = (rows, field, match) =>
    rows
      .filter(match)
      .filter((r) => r[field])
      .reduce((best, r) => (!best || r[field] > best[field] ? r : best), null);

  const request = latest(orders, 'orderedDate',
    (o) => Array.isArray(o.tests) && o.tests.some((t) => /testosterone/i.test(t)));
  const result = latest(results, 'receivedDate',
    (r) => /testosterone/i.test(r.summary || ''));

  if (!request && !result) return { status: 'none', date: null, request: null, result: null };
  if (result && (!request || result.receivedDate >= request.orderedDate)) {
    return { status: 'received', date: result.receivedDate, request, result };
  }
  return { status: 'requested', date: request.orderedDate, request, result };
}

// --- Date helpers -----------------------------------------------------------

/**
 * Whole days from today until `dateIso` (positive = future, negative = past).
 * @param {string} dateIso YYYY-MM-DD
 * @param {string} [today] YYYY-MM-DD, defaults to local today
 */
export function daysUntil(dateIso, today = getLocalToday()) {
  if (!dateIso) return null;
  const a = new Date(today + 'T00:00:00');
  const b = new Date(dateIso + 'T00:00:00');
  return Math.round((b - a) / 86400000);
}

/**
 * True when a monitoring item (vitals / qADAM / labs) is fresh relative to the
 * patient's next monitor date: it has a date, a monitor date is set, and that
 * item was collected no more than MONITOR_WINDOW_DAYS before the monitor date.
 * A null item date, a null monitor date, or an older item is not fresh.
 * @param {string | null} itemDate YYYY-MM-DD — when the item was last collected
 * @param {string | null} monitorDate YYYY-MM-DD — the next monitor date
 */
export function isMonitorItemFresh(itemDate, monitorDate) {
  if (!itemDate || !monitorDate) return false;
  return daysUntil(monitorDate, itemDate) <= MONITOR_WINDOW_DAYS;
}

// --- Column derivation ------------------------------------------------------

/**
 * Single pipeline column for a record:
 *
 *   No testosterone dose                       -> Onboarding
 *   Only a past dose                           -> Inactive
 *   On a dose, monitor date within the window  -> Upcoming
 *   On a dose otherwise                        -> Active
 */
export function getBoardColumn(record, today = getLocalToday()) {
  const status = getTrtDoseStatus(record.trtData);
  if (status === 'none') return COLUMN.ONBOARDING;
  if (status === 'past') return COLUMN.INACTIVE;
  const days = daysUntil(record.monitorDate, today);
  if (days !== null && days <= MONITOR_WINDOW_DAYS) return COLUMN.UPCOMING;
  return COLUMN.ACTIVE;
}
